import { toDateValueSeries } from './series_utils'
import { cssVar, watchChartTheme } from '../../utils/chart_theme'

/**
 * Index price chart (XAO/XJO close line) rendered above the market breadth
 * chart. Deliberately minimal — same datetime axis as the breadth chart so
 * the chart-sync controller can mirror the crosshair between the two. Date
 * labels are omitted: the breadth chart directly below carries them.
 *
 * @param {HTMLElement} container - The DOM element to render the chart in
 * @param {Array} data - [[isoDate, close], ...]
 * @param {Object} options - { chartTitle }
 */
export default function createIndexPriceChart(container, data, options = {}) {
  const seriesData = toDateValueSeries(data)
  const chartTitle = options.chartTitle || 'Index'
  const theme = resolveIndexChartTheme()

  const chartConfig = {
    chart: {
      type: 'spline',
      zoomType: 'x',
      backgroundColor: 'transparent',
      style: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial'
      }
    },
    title: { text: null },
    xAxis: {
      type: 'datetime',
      title: { text: null },
      labels: { enabled: false },
      tickLength: 0,
      crosshair: true
    },
    yAxis: {
      title: { text: chartTitle },
      opposite: false
    },
    tooltip: {
      formatter: function () {
        return '<b>' + Highcharts.dateFormat('%b %e, %Y', this.x) + '</b><br/>' +
          chartTitle + ': <b>' + Highcharts.numberFormat(this.y, 1) + '</b>'
      }
    },
    legend: { enabled: true, verticalAlign: 'top', align: 'center' },
    plotOptions: {
      spline: {
        lineWidth: 2.5,
        color: theme.line,
        marker: {
          enabled: false,
          symbol: 'circle',
          states: {
            hover: {
              enabled: true,
              radius: 5,
              fillColor: theme.line,
              lineWidth: 2,
              lineColor: theme.markerOutline
            }
          }
        },
        animation: { duration: 500 }
      }
    },
    series: [{
      name: chartTitle,
      data: seriesData
    }],
    credits: { enabled: false }
  }

  const chart = Highcharts.chart(container, chartConfig)

  watchChartTheme(chart, () => {
    const next = resolveIndexChartTheme()
    chart.series[0].update({
      color: next.line,
      marker: { states: { hover: { fillColor: next.line, lineColor: next.markerOutline } } }
    })
  })

  return chart
}

// Same line colour as the breadth chart below it, so the pair reads as one
// linked visual.
function resolveIndexChartTheme() {
  return {
    line: cssVar('--bs-body-color', '#212529'),
    markerOutline: cssVar('--bs-body-bg', '#ffffff')
  }
}
